<script>
    import {_} from 'svelte-i18n';
    import {onMount} from 'svelte';
    import {goTo} from '../../utils.js';
    import {battlePassEvent} from '../../stores.js';
    import {refreshBattlePass} from './api.js';
    import {now} from './now.js';
    import Modal from '../Modal.svelte';
    import ItemsListing from '../ItemsListing.svelte';
    import {resourceDetails} from '../../globals.js';
    import BattlePassHeader from './BattlePassHeader.svelte';
    import BattlePassTrack from './BattlePassTrack.svelte';
    import BattlePassPremiumModal from './BattlePassPremiumModal.svelte';
    import checkmark from '../../assets/gold_rush/green_checkmark.png';

    const itemGlobals = { resourceDetails };

    let premiumModalOpen = $state(false);
    let claimResultOpen = $state(false);
    let claimedItems = $state([]);

    let seasonOver = $derived(
        !!$battlePassEvent?.finishAt &&
        $now >= new Date($battlePassEvent.finishAt).getTime()
    );
    let claimWindowOver = $derived(
        !!$battlePassEvent?.claimBy &&
        $now >= new Date($battlePassEvent.claimBy).getTime()
    );

    // Granted items may repeat an id (free + premium lanes) — merge for display
    function mergeItems(items) {
        const byId = {};
        for (const item of items) {
            byId[item.id] = (byId[item.id] || 0) + item.amount;
        }
        return Object.entries(byId).map(([id, amount]) => ({ id: parseInt(id), amount }));
    }

    function handleClaimed(grantedItems) {
        claimedItems = mergeItems(grantedItems || []);
        claimResultOpen = true;
    }

    function openPremiumModal() {
        premiumModalOpen = true;
    }

    // The event is seeded by Shop.svelte's get-packs load; refresh it on open
    // and whenever the tab regains focus so points/progress reflect purchases
    // made in the PaymentHub tab (and any out-of-band changes) without a reload.
    onMount(() => {
        window.scrollTo(0, 0);
        goTo('/supplies/webBattlePass', true);
        localStorage.setItem('webBattlePassSeen', 'true');
        refreshBattlePass();
        window.addEventListener('focus', refreshBattlePass);

        return () => window.removeEventListener('focus', refreshBattlePass);
    });
</script>

{#if Object.keys($battlePassEvent).length !== 0}
    <div class="web-battle-pass">
        <BattlePassHeader event={$battlePassEvent} {seasonOver} {claimWindowOver}
                          onclaimed={handleClaimed} onunlockpremium={openPremiumModal}/>
        {#if !claimWindowOver}
            <BattlePassTrack event={$battlePassEvent}
                             onclaimed={handleClaimed} onunlockpremium={openPremiumModal}/>
        {/if}
    </div>

    <BattlePassPremiumModal event={$battlePassEvent} bind:open={premiumModalOpen}/>

    <Modal class="battle-pass-claim-result" bind:open={claimResultOpen}>
        <div class="claim-result animate__animated animate__bounceIn">
            <img class="result-check" src={checkmark} alt="">
            <h3>{$_('WebBattlePassRewardsClaimed', { default: 'Rewards claimed!' })}</h3>
            <p>{$_('WebBattlePassRewardsSent', { default: 'Your rewards have been sent to your in-game account.' })}</p>
            <ItemsListing globals={itemGlobals} goldAmount={0} items={claimedItems}
                          storePack={{}} hasMask={false}/>
            <button type="button" class="button primary green" onclick={() => claimResultOpen = false}>
                {$_('Ok', { default: 'OK' })}
            </button>
        </div>
    </Modal>
{/if}

<style lang="scss">
  @use './battlepass.scss' as bp;

  // The bounceIn entrance scales .claim-result past 1.0 (to 1.1×). If the modal
  // wrapper (.modal-content) were the scroll container, that overshoot would
  // expand its scrollable overflow and flash both scrollbars mid-bounce. So the
  // wrapper doesn't scroll — the scroll lives on .claim-result itself, which
  // scales as one unit with its content, so the overshoot can't create a scrollbar.
  :global(.modal-content.battle-pass-claim-result) {
    overflow: visible;
  }

  .web-battle-pass {
    max-width: 1240px;
    margin: 0 auto;
    padding: 14px;
    border-radius: 16px;
    background: #240c06;
    border: 1px solid rgba(226, 226, 155, 0.22);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);

    @media (max-width: 700px) {
      margin: 0 6px;
      padding: 8px;
    }
  }

  .claim-result {
    position: relative;
    @include bp.bp-parchment-panel;
    padding: 22px 18px 18px;
    text-align: center;
    width: min(92vw, 460px);
    max-height: 90vh;
    overflow: auto;
    color: var(--color-darkGrey);

    .result-check {
      width: 54px;
      height: auto;
      margin-bottom: 4px;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35));
    }

    h3 {
      margin: 0 0 4px;
      font-family: var(--font-family-sans-condensed);
      font-weight: bold;
      font-size: 1.5em;
      color: #5a2c0e;
    }

    p {
      margin: 0 0 8px;
      font-weight: bold;
      color: #8a6228;
    }

    // Single scroll container: .claim-result itself owns the scroll (max-height
    // 90vh above). The item list must NOT add its own nested scroll or it fights
    // the outer one, producing a cramped inner scrollbox.
    :global(.items),
    :global(.rewards) {
      background: rgba(255, 255, 255, 0.45);
      border-radius: 8px;
      border: 1px solid #d8a93c;
      padding: 4px 10px;
      margin: 0;
    }

    button {
      margin-top: 14px;
      min-width: 200px;
      min-height: 44px;
      font-family: var(--font-family-sans-condensed);
      font-size: 18px;
      padding: 0.4rem 1.5rem;
    }
  }
</style>
